import {
  prisma,
  type Prisma,
  MeetingStatus,
  NotificationsType,
  MeetingType,
  UserRoleAssignmentEntityType,
} from "client";
import { getAllActiveUsersOnEvent } from "../user";
import { transformSurveyResult } from "services/formTemplates";
import { getDayUtcRange } from "../../utils/dayRange";
import {
  applyConnectionAliases,
  uniqueEntityIds,
} from "../connectionRequest/connection-identity.pure";

/** Prisma Mongo findRaw returns Json — pull ObjectId strings out safely. */
function idsFromFindRaw(raw: Prisma.JsonValue): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const doc of raw) {
    if (!doc || typeof doc !== "object" || Array.isArray(doc)) continue;
    const id = doc["_id"];
    if (typeof id === "string") out.push(id);
    else if (id && typeof id === "object" && !Array.isArray(id)) {
      const oid = id["$oid"];
      if (typeof oid === "string") out.push(oid);
    }
  }
  return out;
}

/**
 * Entity ids whose meetings/connections the user may list: self + exhibitor/sponsor
 * companies they own or belong to on this event.
 */
export async function resolveMeetingEntityIds(
  userId: string,
  eventId: string,
  email?: string | null
): Promise<string[]> {
  const ids = new Set<string>([userId]);

  const [exhibitors, sponsors] = await Promise.all([
    prisma.exhibitor.findMany({
      where: {
        eventId,
        OR: [
          { teamIds: { has: userId } },
          ...(email ? [{ email }] : []),
        ],
      },
      select: { id: true },
    }),
    prisma.sponsor.findMany({
      where: {
        eventId,
        OR: [
          { teamIds: { has: userId } },
          ...(email ? [{ email }] : []),
        ],
      },
      select: { id: true },
    }),
  ]);

  for (const e of exhibitors) ids.add(e.id);
  for (const s of sponsors) ids.add(s.id);

  // Mongo equality is case-sensitive. Exact /^email$/i via findRaw (not domain contains).
  if (email) {
    const pattern = `^${email.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`;
    const [exRaw, spRaw] = await Promise.all([
      prisma.exhibitor.findRaw({
        filter: {
          eventId: { $oid: eventId },
          email: { $regex: pattern, $options: "i" },
        },
        options: { projection: { _id: 1 }, limit: 10 },
      }),
      prisma.sponsor.findRaw({
        filter: {
          eventId: { $oid: eventId },
          email: { $regex: pattern, $options: "i" },
        },
        options: { projection: { _id: 1 }, limit: 10 },
      }),
    ]);
    for (const id of idsFromFindRaw(exRaw)) ids.add(id);
    for (const id of idsFromFindRaw(spRaw)) ids.add(id);
  }

  return [...ids];
}

/**
 * Client may pass company id (participantData) when resolveMeetingEntityIds
 * missed email/team edge cases. Only append after ownership check.
 */
export async function appendOwnedCompanyHint(
  entityIds: string[],
  userId: string,
  eventId: string,
  companyId?: string | null,
  email?: string | null
): Promise<string[]> {
  if (!companyId || entityIds.includes(companyId)) return entityIds;

  const ownership: Prisma.ExhibitorWhereInput & Prisma.SponsorWhereInput = {
    id: companyId,
    eventId,
    OR: [
      { teamIds: { has: userId } },
      ...(email ? [{ email }] : []),
    ],
  };

  const [exhibitor, sponsor] = await Promise.all([
    prisma.exhibitor.findFirst({ where: ownership, select: { id: true } }),
    prisma.sponsor.findFirst({ where: ownership, select: { id: true } }),
  ]);

  if (!exhibitor && !sponsor) return entityIds;
  return [...entityIds, companyId];
}

const COMPANY_OWNER_ROLE_NAMES = new Set([
  "EXHIBITOR_SUPER_ADMIN",
  "SPONSOR_SUPER_ADMIN",
]);

/**
 * Company chat/connections are keyed by company id; requests are often stored
 * under the owner user id. Expand both sides with owner + owned-company aliases.
 * Batched, indexed lookups only (id / eventId+teamIds / entityId+entityType).
 */
export async function expandConnectionParticipantSides(
  senderIds: string[],
  receiverIds: string[],
  eventId: string
): Promise<{ senderIds: string[]; receiverIds: string[] }> {
  const seedSenderIds = uniqueEntityIds(senderIds);
  const seedReceiverIds = uniqueEntityIds(receiverIds);
  const seedIds = uniqueEntityIds(seedSenderIds, seedReceiverIds);
  if (!seedIds.length) {
    return { senderIds: seedSenderIds, receiverIds: seedReceiverIds };
  }

  const [exhibitors, sponsors] = await Promise.all([
    prisma.exhibitor.findMany({
      where: { eventId, id: { in: seedIds } },
      select: { id: true, email: true, teamIds: true },
    }),
    prisma.sponsor.findMany({
      where: { eventId, id: { in: seedIds } },
      select: { id: true, email: true, teamIds: true },
    }),
  ]);

  const companies = [...exhibitors, ...sponsors];
  const companyIdSet = new Set(companies.map((company) => company.id));
  const userIds = seedIds.filter((id) => !companyIdSet.has(id));
  const aliasesById = new Map<string, string[]>();

  const addAlias = (id: string, alias: string) => {
    if (!alias || alias === id) return;
    const current = aliasesById.get(id) ?? [];
    if (!current.includes(alias)) aliasesById.set(id, [...current, alias]);
  };

  if (companies.length) {
    const assignments = await prisma.assignmentModel.findMany({
      where: {
        entityId: { in: companies.map((company) => company.id) },
        entityType: {
          in: [
            UserRoleAssignmentEntityType.EXHIBITOR,
            UserRoleAssignmentEntityType.SPONSOR,
          ],
        },
      },
      select: {
        entityId: true,
        userId: true,
        role: { select: { name: true } },
      },
    });
    for (const assignment of assignments) {
      if (
        assignment.entityId &&
        assignment.role?.name &&
        COMPANY_OWNER_ROLE_NAMES.has(assignment.role.name)
      ) {
        addAlias(assignment.entityId, assignment.userId);
      }
    }

    const teamIds = uniqueEntityIds(
      companies.flatMap((company) => company.teamIds)
    );
    if (teamIds.length) {
      const members = await prisma.user.findMany({
        where: { id: { in: teamIds } },
        select: { id: true, email: true },
      });
      const ownerByEmail = new Map<string, string>();
      for (const member of members) {
        if (member.email) ownerByEmail.set(member.email.toLowerCase(), member.id);
      }
      for (const company of companies) {
        if (!company.email) continue;
        const ownerId = ownerByEmail.get(company.email.toLowerCase());
        if (ownerId) addAlias(company.id, ownerId);
      }
    }
  }

  if (userIds.length) {
    const [ownedExhibitors, ownedSponsors] = await Promise.all([
      prisma.exhibitor.findMany({
        where: { eventId, teamIds: { hasSome: userIds } },
        select: { id: true, teamIds: true },
      }),
      prisma.sponsor.findMany({
        where: { eventId, teamIds: { hasSome: userIds } },
        select: { id: true, teamIds: true },
      }),
    ]);
    for (const row of [...ownedExhibitors, ...ownedSponsors]) {
      for (const userId of userIds) {
        if (row.teamIds.includes(userId)) addAlias(userId, row.id);
      }
    }
  }

  return {
    senderIds: applyConnectionAliases(seedSenderIds, aliasesById),
    receiverIds: applyConnectionAliases(seedReceiverIds, aliasesById),
  };
}

/** When the client sends as EXHIBITOR/SPONSOR, use the owned company id if unique. */
export async function resolveOwnedCompanySenderId(
  userId: string,
  eventId: string,
  email: string | null | undefined,
  senderType: string
): Promise<string | undefined> {
  if (senderType !== "EXHIBITOR" && senderType !== "SPONSOR") return undefined;

  const entityIds = await resolveMeetingEntityIds(userId, eventId, email);
  const candidateIds = entityIds.filter((id) => id !== userId);
  if (!candidateIds.length) return undefined;

  const rows =
    senderType === "SPONSOR"
      ? await prisma.sponsor.findMany({
          where: { eventId, id: { in: candidateIds } },
          select: { id: true },
        })
      : await prisma.exhibitor.findMany({
          where: { eventId, id: { in: candidateIds } },
          select: { id: true },
        });

  return rows.length === 1 ? rows[0].id : undefined;
}

/** Pure where builder — kept exportable for a tiny self-check. */
export function buildMyAgendaWhere({
  entityIds,
  eventId,
  search,
  status,
  includeRejected = false,
  meetingType,
  day,
  timezone,
  sessionTypeIds,
}: {
  entityIds: string[];
  eventId: string;
  search?: string;
  status?: MeetingStatus;
  includeRejected?: boolean;
  meetingType?: MeetingType;
  /** Calendar day `YYYY-MM-DD` in event timezone. */
  day?: string;
  timezone?: string;
  sessionTypeIds?: string[];
}): Prisma.MeetingWhereInput {
  const ownership: Prisma.MeetingWhereInput = {
    participants: { some: { entityId: { in: entityIds } } },
  };

  const statusFilter: Prisma.MeetingWhereInput =
    status != null
      ? { status }
      : includeRejected
        ? {}
        : { status: { not: MeetingStatus.REJECTED } };

  // Match the counterparty only — never the caller's own entities.
  const searchFilter: Prisma.MeetingWhereInput | null = search
    ? {
        participants: {
          some: {
            entityId: { notIn: entityIds },
            OR: [
              {
                user: {
                  name: { contains: search, mode: "insensitive" },
                },
              },
              {
                exhibitor: {
                  translations: {
                    some: {
                      name: { contains: search, mode: "insensitive" },
                    },
                  },
                },
              },
              {
                sponsor: {
                  translations: {
                    some: {
                      name: { contains: search, mode: "insensitive" },
                    },
                  },
                },
              },
            ],
          },
        },
      }
    : null;

  const where: Prisma.MeetingWhereInput = {
    eventId,
    ...statusFilter,
    ...(meetingType ? { type: meetingType } : {}),
    ...(sessionTypeIds?.length
      ? { sessionTypeId: { in: sessionTypeIds } }
      : {}),
    AND: [ownership, ...(searchFilter ? [searchFilter] : [])],
  };

  if (day && timezone) {
    const { start, end } = getDayUtcRange(day, timezone);
    where.startDate = { gte: start, lte: end };
  }

  return where;
}

export const getMyAgenda = async (
  userId: string | string[],
  eventId: string,
  lang: string = "en",
  page: number = 1,
  limit: number = 10,
  type?: string[],
  day?: string,
  search?: string,
  status?: MeetingStatus,
  meetingType?: MeetingType,
  includeRejected: boolean = false,
  timezone?: string
) => {
  try {
    const entityIds = Array.isArray(userId) ? userId : [userId];
    // Connections are user↔user — always the first id (caller puts session user first).
    const connectionUserId = entityIds[0];

    const whereClause = buildMyAgendaWhere({
      entityIds,
      eventId,
      search,
      status,
      includeRejected,
      meetingType,
      day,
      timezone,
      sessionTypeIds: type,
    });

    const [sessionMeetings, totalCount] = await Promise.all([
      prisma.meeting.findMany({
        where: whereClause,
        include: {
          notifications: {
            where: {
              type: NotificationsType.SEND_MEETING_REQUEST,
            },
          },
          formResults: {
            include: {
              formResult: {
                include: {
                  formTemplate: true,
                },
              },
            },
          },
          meetingRoom: {
            include: {
              translations: true,
            },
          },
          participants: {
            include: {
              exhibitor: {
                include: {
                  translations: {
                    where: {
                      language: {
                        code: lang,
                      },
                    },
                  },
                },
              },
              sponsor: {
                include: {
                  translations: {
                    where: {
                      language: {
                        code: lang,
                      },
                    },
                  },
                },
              },
              user: {
                include: {
                  profiles: {
                    where: {
                      eventId,
                    },
                    take: 1,
                    orderBy: {
                      createdAt: "desc",
                    },
                  },
                  networking: true,
                  tags: {
                    where: {
                      eventId,
                    },
                  },
                  ...(connectionUserId && {
                    connectionRequestsReceived: {
                      where: {
                        OR: [
                          { senderId: connectionUserId },
                          { receiverId: connectionUserId },
                        ],
                      },
                      select: {
                        senderId: true,
                        receiverId: true,
                        status: true,
                      },
                    },
                    connectionRequestsSent: {
                      where: {
                        OR: [
                          { senderId: connectionUserId },
                          { receiverId: connectionUserId },
                        ],
                      },
                      select: {
                        senderId: true,
                        receiverId: true,
                        status: true,
                      },
                    },
                  }),
                },
              },
            },
          },
          translations: {
            where: {
              language: {
                code: lang,
              },
            },
          },
          CalendarIntegrationMeeting: true,
        },
        skip: (page - 1) * limit,
        take: limit,
        orderBy: {
          startDate: "asc",
        },
      }),
      prisma.meeting.count({
        where: whereClause,
      }),
    ]);
    const transformedData = sessionMeetings.map((meeting) => {
      return {
        ...meeting,
        formResults: meeting.formResults?.map((formResult) => {
          return transformSurveyResult(
            formResult.formResult?.formTemplate?.json,
            formResult.formResult?.surveyResult
          );
        }),
      };
    });

    return {
      data: transformedData,
      pagination: {
        total: totalCount,
        currentPage: page,
        totalPages: Math.ceil(totalCount / limit),
        hasNextPage: page < Math.ceil(totalCount / limit),
        hasPreviousPage: page > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching my agenda:", error);
    throw new Error("Failed to fetch my agenda");
  }
};

export const getMyConnections = async (
  userId: string,
  eventId: string,
  lang: string = "en",
  page: number = 1,
  limit: number = 10
) => {
  try {
    const activeUserIds = await getAllActiveUsersOnEvent(eventId);

    const whereClause: Prisma.ConnectionRequestWhereInput = {
      OR: [
        {
          senderId: userId,
          receiverId: { in: activeUserIds },
        },
        {
          receiverId: userId,
          senderId: { in: activeUserIds },
        },
      ],
      eventId,
    };
    const [connections, totalCount] = await Promise.all([
      prisma.connectionRequest.findMany({
        where: whereClause,
        include: {
          receiverUser: {
            include: {
              bookmarks: {
                where: {
                  userId,
                },
              },
              networking: true,
              tags: true,
            },
          },
        },
      }),
      prisma.connectionRequest.count({
        where: whereClause,
      }),
    ]);
    const transformedData = connections.map((connection) => {
      const isBookmarked = !!(
        connection?.receiverUser?.bookmarks &&
        connection?.receiverUser?.bookmarks?.length > 0
      );
      return {
        ...connection,
        isBookmarked: isBookmarked,
      };
    });
    return {
      data: transformedData,
      total: totalCount,
      page,
      totalPages: Math.ceil(totalCount / limit),
      hasNextPage: page < Math.ceil(totalCount / limit),
    };
  } catch (error) {
    console.error("Error fetching my connections:", error);
    throw new Error("Failed to fetch my connections");
  }
};

export const getUserInterest = async (eventId: string, userId: string) => {
  try {
    return await prisma.tag.findMany({
      where: {
        eventId,
        users: {
          some: {
            id: userId,
          },
        },
      },
    });
  } catch (error) {
    console.error("Error fetching user interest:", error);
    throw new Error("Failed to fetch user interest");
  }
};
